import type { RouterOutputs } from '@client/trpc/client';
import {
    QUESTIONNAIRE_EXPORT_FORMAT,
    QUESTIONNAIRE_EXPORT_VERSION,
    type QuestionnaireExportEnvelope,
    questionnaireExportEnvelopeSchema,
} from '@shared/schemas';
import type { CardDef } from '@shared/types';
import { downloadBlob } from '../common/blob';

type QuestionnaireRecord = RouterOutputs['questionnaire']['getQuestionnaire'];

export function buildQuestionnaireExportEnvelope(record: QuestionnaireRecord): QuestionnaireExportEnvelope {
    return {
        format: QUESTIONNAIRE_EXPORT_FORMAT,
        version: QUESTIONNAIRE_EXPORT_VERSION,
        exportedAt: new Date().toISOString(),
        sourceOrgId: record.orgId.toString(),
        questionnaire: {
            name: record.name,
            definition: record.definition as QuestionnaireExportEnvelope['questionnaire']['definition'],
            declarationTitle: record.declarationTitle,
            declarationBody: record.declarationBody,
            confirmationBody: record.confirmationBody,
            notificationSendTo: record.notificationSendTo,
            subject: record.subject,
            emailBody: record.emailBody,
            smsBody: record.smsBody,
            logoUrl: record.logoUrl,
            faviconUrl: record.faviconUrl,
            theme: record.theme,
        },
    };
}

export function downloadQuestionnaireExport(record: QuestionnaireRecord): void {
    const envelope = buildQuestionnaireExportEnvelope(record);
    const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `${sanitiseFileName(record.name)}.questionnaire.json`);
}

export type ParseQuestionnaireExportResult =
    | { ok: true; envelope: QuestionnaireExportEnvelope }
    | { ok: false; reason: 'not-json' | 'wrong-format' | 'unsupported-version' | 'invalid-fields'; detail?: string };

export function parseQuestionnaireExport(text: string): ParseQuestionnaireExportResult {
    let raw: unknown;
    try {
        raw = JSON.parse(text);
    } catch {
        return { ok: false, reason: 'not-json' };
    }

    if (
        typeof raw !== 'object' ||
        raw === null ||
        (raw as { format?: unknown }).format !== QUESTIONNAIRE_EXPORT_FORMAT
    ) {
        return { ok: false, reason: 'wrong-format' };
    }
    if ((raw as { version?: unknown }).version !== QUESTIONNAIRE_EXPORT_VERSION) {
        return {
            ok: false,
            reason: 'unsupported-version',
            detail: String((raw as { version?: unknown }).version),
        };
    }

    const parsed = questionnaireExportEnvelopeSchema.safeParse(raw);
    if (!parsed.success) {
        return {
            ok: false,
            reason: 'invalid-fields',
            detail: parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; '),
        };
    }

    return { ok: true, envelope: parsed.data };
}

export interface OrgScopedReferences {
    /** SPECIFIED_ITEM children pointing at org/PDS-scoped item ids — need re-mapping after a cross-org import. */
    specifiedItems: Array<{ cardId: string; cardTitle: string; childTitle: string }>;
    /** UPLOAD children tagged with file category names configured per organisation. */
    uploadFileCategories: Array<{ cardId: string; cardTitle: string; categories: string[] }>;
}

export function collectOrgScopedReferences(cards: CardDef[]): OrgScopedReferences {
    const result: OrgScopedReferences = { specifiedItems: [], uploadFileCategories: [] };

    for (const card of cards) {
        if (!('children' in card) || !Array.isArray(card.children)) {
            continue;
        }
        const cardTitle = card.title || card.internalName || card.id;
        for (const child of card.children) {
            if (child.type === 'SPECIFIED_ITEM' && (child.specifiedItemId != null || child.pdsConfigItemId != null)) {
                result.specifiedItems.push({ cardId: card.id, cardTitle, childTitle: child.title || child.id });
            }
            if (child.type === 'UPLOAD' && Array.isArray(child.fileCategories) && child.fileCategories.length > 0) {
                result.uploadFileCategories.push({ cardId: card.id, cardTitle, categories: child.fileCategories });
            }
        }
    }

    return result;
}

export function resolveImportName(name: string, existingNames: ReadonlySet<string>, suffix = 'imported'): string {
    if (!existingNames.has(name)) {
        return name;
    }
    const base = `${name} (${suffix})`;
    if (!existingNames.has(base)) {
        return base;
    }
    for (let i = 2; ; i++) {
        const candidate = `${name} (${suffix} ${i})`;
        if (!existingNames.has(candidate)) {
            return candidate;
        }
    }
}

/**
 * Maps a full questionnaire record onto a createQuestionnaire input —
 * everything except the caller-chosen name. Used by duplicate-in-place.
 */
export function questionnaireRecordToCreateInput(record: QuestionnaireRecord) {
    return {
        definition: record.definition ?? undefined,
        declarationTitle: record.declarationTitle,
        declarationBody: record.declarationBody,
        confirmationBody: record.confirmationBody,
        notificationSendTo: record.notificationSendTo ?? undefined,
        subject: record.subject,
        emailBody: record.emailBody,
        smsBody: record.smsBody,
        logoUrl: record.logoUrl,
        faviconUrl: record.faviconUrl,
        theme: record.theme ?? undefined,
    };
}

function sanitiseFileName(name: string): string {
    const cleaned = name
        .replace(/[/\\?%*:|"<>]/g, '')
        .trim()
        .replace(/\s+/g, ' ');
    return cleaned || 'questionnaire';
}
